// useAnalytics.ts — Fetches site analytics from the Railway /admin/analytics endpoint.
//
// Cash B1.8 built this endpoint (commit 99b40a5). Authenticates with
// VITE_ADMIN_API_TOKEN (Bearer token — shared secret, never principal-derived).
//
// No mock fallback per Jerry's Phase B decision: live data only.
// Empty state when endpoint is not configured or returns zero data.

import { useCallback, useEffect, useRef, useState } from 'react';

export interface TopPage {
  path: string;
  views: number;
}

export interface TrafficSource {
  name: string;
  visitors: number;
  share: number;   // 0-100 percentage
}

export interface AnalyticsData {
  visitsToday: number;
  visits7d: number;
  visits30d: number;
  uniqueToday: number;
  unique7d: number;
  unique30d: number;
  bounceRate: number;    // percentage 0-100
  conversion7d: number;  // percentage 0-100
  topPages: TopPage[];
  trafficSources: TrafficSource[];
  fetchedAt: string;     // ISO datetime
}

export interface UseAnalyticsReturn {
  data: AnalyticsData | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

const POLL_INTERVAL_MS = 5 * 60_000; // 5 minutes — Cloudflare data is not real-time

export function useAnalytics(): UseAnalyticsReturn {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);

  const fetchAnalytics = useCallback(async () => {
    const endpoint = import.meta.env.VITE_ADMIN_ANALYTICS_URL;
    const token = import.meta.env.VITE_ADMIN_API_TOKEN;

    // No mock fallback per Phase B decision. No endpoint = empty state.
    if (!endpoint) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(endpoint, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        throw new Error(`Analytics endpoint returned ${response.status}`);
      }

      const json: AnalyticsData = await response.json();

      if (isMountedRef.current) {
        setData(json);
      }
    } catch (err) {
      if (isMountedRef.current) {
        const msg =
          err instanceof Error ? err.message : 'Analytics endpoint unreachable.';
        setError(msg);
      }
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }, []);

  const refresh = useCallback(() => { fetchAnalytics(); }, [fetchAnalytics]);

  useEffect(() => {
    isMountedRef.current = true;
    fetchAnalytics();
    timerRef.current = setInterval(fetchAnalytics, POLL_INTERVAL_MS);
    return () => {
      isMountedRef.current = false;
      if (timerRef.current !== null) clearInterval(timerRef.current);
    };
  }, [fetchAnalytics]);

  return { data, isLoading, error, refresh };
}
